import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Shield, ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";

// HTTP Status codes
const HTTP_FORBIDDEN = 403;

const PRIORITY_LABELS = {
  1: "Basse",
  2: "Moyenne",
  3: "Haute"
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [wishes, setWishes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllWishes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllWishes = async () => {
    try {
      const response = await axios.get(`${API}/admin/wishes`, { withCredentials: true });
      setWishes(response.data);
    } catch (error) {
      if (error.response?.status === HTTP_FORBIDDEN) {
        toast.error("Accès non autorisé");
        navigate('/admin');
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (wishes.length === 0) {
      toast.error("Aucun souhait à exporter");
      return;
    }

    // CSV Headers
    const headers = [
      "ID",
      "Cadeau",
      "Catégorie",
      "Taille",
      "Priorité",
      "Statut",
      "Propriétaire",
      "Username",
      "Réservé",
      "Réservé par",
      "Lien du produit",
      "Prix",
      "Date de création"
    ];

    // CSV Rows
    const rows = wishes.map(wish => [
      wish.wish_id,
      `"${(wish.item_name || '').replace(/"/g, '""')}"`,
      wish.category || '',
      wish.size || '',
      PRIORITY_LABELS[wish.priority] || wish.priority,
      wish.status || 'disponible',
      `"${(wish.owner_name || '').replace(/"/g, '""')}"`,
      wish.owner_username || '',
      wish.is_reserved ? 'Oui' : 'Non',
      wish.reserved_by || '',
      wish.link || '',
      wish.og_price || '',
      new Date(wish.created_at).toLocaleDateString('fr-FR')
    ]);

    // Build CSV content
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');

    // Add BOM for Excel compatibility with French characters
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Download file
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `giftme_souhaits_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`${wishes.length} souhaits exportés avec succès !`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      {/* Header */}
      <header className="bg-card/80 backdrop-blur-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="font-heading text-xl font-bold" data-testid="admin-dashboard-title">Administration</h1>
              <p className="text-sm text-muted-foreground">Tous les souhaits de la plateforme</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              data-testid="users-btn"
              onClick={() => navigate('/admin/users')} 
              variant="outline" 
              size="sm"
              className="rounded-full"
            >
              Utilisateurs
            </Button>
            <Button 
              data-testid="back-btn"
              onClick={() => navigate('/')} 
              variant="ghost" 
              size="sm"
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Card className="shadow-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading" data-testid="total-wishes">Total: {wishes.length} souhaits</CardTitle>
            <Button 
              data-testid="export-csv-btn"
              onClick={exportToCSV}
              variant="outline"
              size="sm"
              className="rounded-full"
              disabled={wishes.length === 0}
            >
              <Download className="w-4 h-4 mr-2" />
              Exporter CSV
            </Button>
          </CardHeader>
          <CardContent>
            {wishes.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Aucun souhait dans la base de données</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cadeau</TableHead>
                      <TableHead>Catégorie</TableHead>
                      <TableHead>Taille</TableHead>
                      <TableHead>Priorité</TableHead>
                      <TableHead>Propriétaire</TableHead>
                      <TableHead>Username</TableHead>
                      <TableHead>Réservé par</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {wishes.map((wish) => (
                      <TableRow key={wish.wish_id} data-testid={`admin-wish-${wish.wish_id}`}>
                        <TableCell className="font-medium">{wish.item_name}</TableCell>
                        <TableCell>{wish.category}</TableCell>
                        <TableCell>{wish.size || '-'}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            wish.priority === 3 ? 'bg-primary text-primary-foreground' :
                            wish.priority === 2 ? 'bg-accent text-accent-foreground' :
                            'bg-muted text-muted-foreground'
                          }`}>
                            {PRIORITY_LABELS[wish.priority]}
                          </span>
                        </TableCell>
                        <TableCell>{wish.owner_name}</TableCell>
                        <TableCell className="font-mono text-sm">@{wish.owner_username}</TableCell>
                        <TableCell>
                          {wish.is_reserved ? (
                            <span className="text-secondary font-medium">{wish.reserved_by}</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(wish.created_at).toLocaleDateString('fr-FR')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}